import React, { useState, useRef } from 'react';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './AddDishModal.css';

const DISH_TYPES = [
  { value: 'Pizza', emoji: '🍕', label: 'Pizza' },
  { value: 'Pasta', emoji: '🍝', label: 'Pasta' },
  { value: 'Burger', emoji: '🍔', label: 'Burger' },
  { value: 'Sushi', emoji: '🍣', label: 'Sushi' },
  { value: 'Taco', emoji: '🌮', label: 'Taco' },
  { value: 'Ramen', emoji: '🍜', label: 'Ramen' },
  { value: 'Curry', emoji: '🍛', label: 'Curry' },
  { value: 'Steak', emoji: '🥩', label: 'Steak' },
  { value: 'Seafood', emoji: '🦞', label: 'Seafood' },
  { value: 'Dessert', emoji: '🍰', label: 'Dessert' },
  { value: 'Salad', emoji: '🥗', label: 'Salad' },
  { value: 'Soup', emoji: '🍲', label: 'Soup' },
  { value: 'Appetizer', emoji: '🥟', label: 'Appetizer' },
  { value: 'Breakfast', emoji: '🥞', label: 'Breakfast' },
  { value: 'Sandwich', emoji: '🥪', label: 'Sandwich' },
  { value: 'Other', emoji: '🍽️', label: 'Other' }
];

const CUISINES = [
  'Italian', 'Mexican', 'Japanese', 'Chinese', 'Indian', 
  'Thai', 'French', 'American', 'Mediterranean', 'Korean',
  'Vietnamese', 'Greek', 'Spanish', 'Middle Eastern', 'Other'
];

function AddDishModal({ onClose, onAdd }) {
  const [name, setName] = useState('');
  const [dishType, setDishType] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const restaurantInputRef = useRef(null);

  // Google Places Autocomplete (using Places API)
  const initGooglePlaces = () => {
    if (window.google && window.google.maps && window.google.maps.places) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        restaurantInputRef.current,
        {
          types: ['establishment'],
          fields: ['name', 'formatted_address', 'place_id']
        }
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.name) {
          setRestaurantName(place.name);
          if (place.formatted_address) {
            setLocation(place.formatted_address);
          }
        }
      });
    }
  };

  React.useEffect(() => {
    // Load Google Places API script if not already loaded
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY || 'YOUR_API_KEY'}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (restaurantInputRef.current) {
          initGooglePlaces();
        }
      };
      document.head.appendChild(script);
    } else if (restaurantInputRef.current) {
      initGooglePlaces();
    }
  }, []);

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return null;

    try {
      setUploading(true);
      const storageRef = ref(storage, `dish-images/${Date.now()}_${imageFile.name}`);
      await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Please enter a dish name');
      return;
    }

    setUploading(true);
    let imageUrl = null;

    // Upload image if provided
    if (imageFile) {
      imageUrl = await uploadImage();
      if (!imageUrl) {
        setUploading(false);
        return;
      }
    }

    onAdd({
      name: name.trim(),
      dishType: dishType || null,
      cuisine: cuisine || null,
      restaurantName: restaurantName.trim() || null,
      location: location.trim() || null,
      imageUrl: imageUrl,
      description: description.trim() || null
    });

    // Reset form
    setName('');
    setDishType('');
    setCuisine('');
    setRestaurantName('');
    setLocation('');
    setDescription('');
    setImageFile(null);
    setImagePreview(null);
    setUploading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Dish</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="name">Dish Name *</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g., Margherita Pizza"
            />
          </div>

          <div className="form-group">
            <label>Dish Type</label>
            <div className="emoji-buttons">
              {DISH_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  className={`emoji-button ${dishType === type.value ? 'selected' : ''}`}
                  onClick={() => setDishType(dishType === type.value ? '' : type.value)}
                  title={type.label}
                >
                  <span className="emoji-icon">{type.emoji}</span>
                  <span className="emoji-label">{type.label}</span>
                </button>
              ))}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="cuisine">Cuisine</label>
            <select
              id="cuisine"
              value={cuisine}
              onChange={(e) => setCuisine(e.target.value)}
            >
              <option value="">Select cuisine...</option>
              {CUISINES.map(cuisineOption => (
                <option key={cuisineOption} value={cuisineOption}>{cuisineOption}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="restaurantName">Restaurant Name</label>
            <input
              ref={restaurantInputRef}
              type="text"
              id="restaurantName"
              value={restaurantName}
              onChange={(e) => setRestaurantName(e.target.value)}
              placeholder="Start typing restaurant name (Google Places)"
              autoComplete="off"
            />
            <small className="form-hint">Start typing to search Google Places</small>
          </div>
          
          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              type="text"
              id="location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., New York, NY"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Why did you like this dish? (Optional)</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Share what made this dish special - the flavors, texture, presentation, or anything else that stood out..."
              rows="4"
            />
            <small className="form-hint">Help others understand what makes this dish great!</small>
          </div>
          
          <div className="form-group">
            <label>Dish Photo</label>
            <div
              className={`image-upload-area ${isDragging ? 'dragging' : ''} ${imagePreview ? 'has-image' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {imagePreview ? (
                <div className="image-preview-container">
                  <img src={imagePreview} alt="Preview" className="image-preview" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <span className="upload-icon">📷</span>
                  <p className="upload-text">Drag & drop your photo here</p>
                  <p className="upload-subtext">or click to browse</p>
                  <p className="upload-hint">Max size: 5MB</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>
          
          <div className="modal-actions">
            <button type="button" onClick={onClose} className="btn-cancel" disabled={uploading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Add Dish'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddDishModal;
